/**
 * Anrufen — und den Anruf danach festhalten.
 *
 * Der Knopf wählt (`tel:`) und öffnet unmittelbar den Dialog fürs Ergebnis. Die
 * Reihenfolge ist Absicht: Wer erst nach dem Auflegen an die Dokumentation
 * denkt, dokumentiert nicht. Der Dialog läuft nebenher mit, misst die Dauer und
 * fragt am Ende nur noch das, was niemand sonst weiß — erreicht oder nicht, und
 * was besprochen wurde.
 *
 * Eine Komponente für das ganze Portal: Ticket-Cockpit, Chat-Inbox,
 * Rückrufliste, unbekannte Anrufer, Kontaktverzeichnis, Kundenakte. Damit ist
 * das Ergebnis überall dieselbe Frage, und der Verlauf eines Kunden bleibt
 * vollständig, egal aus welcher Ecke telefoniert wurde.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { ActionIcon, Button, Tooltip } from '@mantine/core';
import { IconPhone } from '@tabler/icons-react';
import type { CallOriginType } from '../../lib/api';
import { CallOutcomeModal } from './CallOutcomeModal';
import { dialNumber, forgetPendingCall, pendingCallKey, takePendingCall } from './pendingCall';

export interface CallTargetTicket {
  provider: 'weclapp' | 'connectwise';
  id: string;
  /** Sprechende Nummer für den Verlauf (#T26-02668). */
  number?: string;
}

export interface CallButtonProps {
  /** Rufnummer, beliebig formatiert. Fehlt sie, wird nichts gerendert. */
  phone: string | null | undefined;
  /** Wen man erreichen will — steht später im Verlauf. */
  contactName?: string | null;
  customerId?: string | null;
  contactId?: string | null;
  /** Ticketbezug: erzeugt zusätzlich einen INTERNEN Vermerk am Ticket. */
  ticket?: CallTargetTicket | null;
  /** Woher der Anruf ausgelöst wurde. */
  originType?: CallOriginType;
  originId?: string | null;
  /** `icon` = runder Knopf, `button` = beschrifteter Knopf. */
  variant?: 'icon' | 'button';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  label?: string;
  /** Wird nach dem Speichern aufgerufen — z. B. um einen Verlauf neu zu laden. */
  onLogged?: () => void;
  fullWidth?: boolean;
  /**
   * Eigener Auslöser statt der beiden Standard-Knöpfe.
   *
   * Für Stellen, an denen der Knopf schon eine feste Gestalt hat (im
   * Ticket-Cockpit heißt er „Melder anrufen" und sitzt in einer Reihe
   * gleichrangiger Aktionen). Geteilt wird dann das VERHALTEN — wählen,
   * mitlaufende Uhr, Ergebnis festhalten —, nicht das Aussehen; ein Knopf, der
   * sich je nach Seite anders anfühlt, wäre der schlechtere Tausch.
   */
  children?: (dial: () => void) => ReactNode;
}

export function CallButton({
  phone,
  contactName,
  customerId,
  contactId,
  ticket,
  originType,
  originId,
  variant = 'icon',
  size = 'sm',
  label = 'Anrufen',
  onLogged,
  fullWidth,
  children,
}: CallButtonProps) {
  const [startedAt, setStartedAt] = useState<Date | null>(null);
  const key = pendingCallKey([phone, originType, originId, ticket?.id]);

  // Kam die Seite nach dem Wechsel in die Telefon-App neu geladen zurück, wird
  // der angefangene Anruf wieder aufgemacht — sonst verschwände genau der kurze
  // Fehlversuch, den festzuhalten der eigentliche Zweck ist.
  useEffect(() => {
    const pending = takePendingCall(key);
    if (pending) setStartedAt(new Date(pending.startedAt));
  }, [key]);

  if (!phone?.trim()) return null;

  const dial = () => {
    setStartedAt(
      dialNumber({
        key,
        phone,
        contactName: contactName ?? null,
        customerId: customerId ?? null,
        contactId: contactId ?? null,
        ticket: ticket ?? null,
        ...(originType ? { originType } : {}),
        originId: originId ?? null,
      }),
    );
  };

  const tooltip = contactName ? `${contactName} anrufen (${phone})` : `${phone} anrufen`;

  return (
    <>
      {children ? (
        children(dial)
      ) : variant === 'icon' ? (
        <Tooltip label={tooltip} withArrow>
          <ActionIcon
            size={size === 'xs' ? 'sm' : 'lg'}
            variant="light"
            color="teal"
            onClick={dial}
            aria-label={tooltip}
            data-testid="call-button"
          >
            <IconPhone size={16} />
          </ActionIcon>
        </Tooltip>
      ) : (
        <Button
          size={size === 'lg' ? 'sm' : 'xs'}
          variant="light"
          color="teal"
          leftSection={<IconPhone size={14} />}
          onClick={dial}
          fullWidth={fullWidth}
          data-testid="call-button"
        >
          {label}
        </Button>
      )}

      <CallOutcomeModal
        opened={startedAt !== null}
        onClose={() => {
          forgetPendingCall();
          setStartedAt(null);
        }}
        startedAt={startedAt}
        phone={phone}
        contactName={contactName ?? null}
        customerId={customerId ?? null}
        contactId={contactId ?? null}
        ticket={ticket ?? null}
        originType={originType}
        originId={originId ?? null}
        onLogged={onLogged}
      />
    </>
  );
}
